<template>
  <div>
    <h2 class="h3 mt-4 mb-3">Filter Nominations out of Events</h2>
    <div class="mb-4">
      <f-radio-group
        name="filterBy"
        label="Filter by"
        :value="filterBy"
        :options="filterByOptions"
        inline
        @input="onFilterByChange"
      />
      <div class="col-12 col-xl-6 form-group pl-0">
        <f-search-select
          id="filter_event"
          label="Select event"
          :value="selectedEventId"
          :options="events"
          :searchable="false"
          @input="selectedEventId = Number($event)"
        />
      </div>
      <div class="col-12 col-xl-6 form-group pl-0">
        <f-search-select
          id="filter_status"
          :label="filterBy === 'horse' ? 'Select horse status' : 'Select rider status'"
          :value="selectedTypeId"
          :options="statusOptions"
          :searchable="false"
          @input="selectedTypeId = Number($event)"
        />
      </div>
      <div class="my-3">
        <app-button
          variant="primary"
          :block="false"
          class="px-xl-5"
          :loading="loadingStats"
          :disabled="selectedEventId == null || selectedTypeId == null"
          @click="onShow"
        >Show</app-button>
      </div>
      <form-alert :message="alert.message.value" :type="alert.type.value" class="mt-2" />
      <template v-if="statistics != null">
        <div class="mt-2 d-flex">
          Total: <strong class="pl-1">{{ statistics.total }}</strong>.
          {{ filterBy === 'horse' ? 'Horses' : 'Riders' }}: <strong class="pl-1">{{ statistics.grouped }}</strong>.
        </div>
        <div class="my-3">
          <app-button
            variant="primary"
            :block="false"
            class="px-xl-5"
            :loading="scratching"
            :disabled="statistics.total === 0"
            @click="confirmScratch = true"
          >Scratch all</app-button>
        </div>
      </template>
    </div>

    <confirm-modal
      v-if="confirmScratch"
      message="Are you sure you want to scratch all filtered nominations? This cannot be undone."
      :loading="scratching"
      @confirm="onScratchAll"
      @cancel="confirmScratch = false"
    />
  </div>
</template>

<script>
import { defineComponent, ref, watch, onMounted } from '@vue/composition-api'
import FRadioGroup from '@/components/FormFields/FRadioGroup'
import FSearchSelect from '@/components/FormFields/FSearchSelect'
import AppButton from '@/components/AppButton'
import FormAlert from '@/components/FormAlert'
import ConfirmModal from '@/components/ConfirmModal'
import useFilterNominations from '@/composables/drafts/useFilterNominations'
import useFormAlert from '@/composables/useFormAlert'

const FILTER_BY_OPTIONS = [
  { value: 'horse', label: 'Horse' },
  { value: 'rider', label: 'Rider' }
]

export default defineComponent({
  name: 'DraftFilterNominations',
  components: { FRadioGroup, FSearchSelect, AppButton, FormAlert, ConfirmModal },
  props: {
    events: { type: Array, default: () => [] }
  },
  setup(props) {
    const {
      filterBy, selectedEventId, selectedTypeId, statistics, loadingStats, scratching,
      statusOptions, loadFilterStatuses, setFilterBy, fetchStatistics, scratchAll
    } = useFilterNominations()
    const alert = useFormAlert()
    const confirmScratch = ref(false)

    function onFilterByChange(val) {
      setFilterBy(val)
    }

    function onShow() {
      alert.clear()
      fetchStatistics()
    }

    async function onScratchAll() {
      confirmScratch.value = false
      alert.clear()
      try {
        const result = await scratchAll()
        alert.showSuccess(result.message)
      } catch (err) {
        const data = err.response != null ? err.response.data : null
        alert.showError(data != null && data.message != null ? data.message : 'Failed to scratch nominations')
      }
    }

    function tryAutoFetch() {
      if (selectedEventId.value != null && selectedTypeId.value != null && statistics.value == null) {
        fetchStatistics()
      }
    }

    watch(() => props.events, (events) => {
      if (events.length > 0 && selectedEventId.value == null) {
        selectedEventId.value = events[0].id
        tryAutoFetch()
      }
    })

    watch(selectedTypeId, () => {
      tryAutoFetch()
    })

    onMounted(() => {
      loadFilterStatuses()
    })

    return {
      filterByOptions: FILTER_BY_OPTIONS,
      filterBy,
      selectedEventId,
      selectedTypeId,
      statistics,
      loadingStats,
      scratching,
      statusOptions,
      alert,
      confirmScratch,
      onFilterByChange,
      onShow,
      onScratchAll
    }
  }
})
</script>



// WEBPACK FOOTER //
// src/views/Drafts/View/components/DraftFilterNominations.vue